import React, { useState } from 'react'
import classNames from 'classnames'

import {
  CheckBox,
  IconButton,
  Input,
  INPUT_THEME,
  Pagination,
  usePagination,
} from '@st-operations/uikit/src/v2'

import Select from '_components/select'

import { SCORE_RANGE, SCORE_TO_LABEL } from '../tab-candidate-urls-list'

import './styles.scss'

const Header = ({
  columns,
  sortedBy,
  setSortedBy,
  checkAllState,
  handleCheckAll,
  isCheckboxDisabled,
}) => {
  return (
    <thead>
      <tr>
        <th>
          <CheckBox
            checked={checkAllState === 'checked'}
            isIndeterminate={checkAllState === 'indeterminate'}
            onClick={handleCheckAll(checkAllState)}
            disabled={isCheckboxDisabled}
          />
        </th>
        <For each="column" of={columns}>
          <th key={column.key}>
            <span style={{ marginRight: 4 }}>{column.name}</span>
            <If condition={column.sortable}>
              <IconButton
                text="Sort"
                ariaLabel="Sort column"
                iconProps={{ icon: 'sort', width: 16, height: 16, color: 'currentColor' }}
                className="st-table__sort-button"
                isActive={sortedBy === column.key}
                onClick={() => setSortedBy(column.key)}
              />
            </If>
          </th>
        </For>
      </tr>
    </thead>
  )
}

const Row = ({ item, columns, handleCheckbox, isCheckboxDisabled }) => {
  return (
    <tr className={classNames({ 'omm-table__row-checked': item.selected })}>
      <td>
        <CheckBox
          checked={item.selected}
          onChange={handleCheckbox(item)}
          disabled={isCheckboxDisabled}
        />
      </td>
      <For each="column" of={columns}>
        <td
          key={column.key}
          className={column.class}
          title={
            item[column.tooltip] ? column.tooltipFormat(item[column.tooltip]) : item[column.key]
          }
        >
          {column.format ? column.format(item[column.key]) : item[column.key]}
        </td>
      </For>
    </tr>
  )
}

const sortingBy = (key) => (a, b) => {
  if (key === 'canonicalUrl') {
    if (a[key] < b[key]) {
      return -1
    }
    if (a[key] > b[key]) {
      return 1
    }
    return 0
  }
  if (a[key] < b[key]) {
    return 1
  }
  if (a[key] > b[key]) {
    return -1
  }
  return 0
}

// checkbox urls score users users% events events% internal_users internal_users%
const MAIN_COLUMNS = 'min-content 1fr 130px 70px 70px 70px 70px 125px 125px'
// const WITH_EXTRA = 'min-content 1fr 130px 70px 90px 70px 70px 70px 110px 110px' // value when fake news is on.
const WITH_EXTRA = 'min-content 1fr 130px 70px 70px 70px 70px 125px 125px 110px'

const Table = ({ columns: rawColumns, data, updateSelected }) => {
  const [sortedBy, setSortedBy] = useState('score')
  const [query, setQuery] = useState('')
  const [scoreFilter, setScoreFilter] = useState(null)
  const [showExtraColumns, setShowExtraColumns] = useState(false)

  // selected urls is set after audience is either delivered or requested reports
  // in both cases, it should not change anymore otherwise the two flows won't match
  const isCheckboxDisabled = data?.selectedUrls?.length

  const SCORE_OPTIONS = Object.values(SCORE_TO_LABEL).map((name) => ({ key: name, label: name }))

  const columns = showExtraColumns ? rawColumns : rawColumns.filter(({ extra }) => !extra)
  const columnsSize = showExtraColumns ? WITH_EXTRA : MAIN_COLUMNS

  const filteredScores = scoreFilter
    ? data.candidateUrls.filter(
        ({ score }) =>
          score <= SCORE_RANGE[scoreFilter].max && score >= SCORE_RANGE[scoreFilter].min
      )
    : data.candidateUrls

  const filteredData = query
    ? filteredScores.filter(({ canonicalUrl }) => canonicalUrl.includes(query))
    : filteredScores

  const sortedData = [...filteredData].sort(sortingBy(sortedBy))

  const MAX_PAGE_SIZE = 100
  const { page: dataPaginated, toPage } = usePagination(sortedData, MAX_PAGE_SIZE, 1)

  const handleCheckbox = (item) => (event) => {
    if (event.target.checked) {
      updateSelected('add', [item])
    } else {
      updateSelected('remove', [item])
    }
  }

  const handleCheckAll = (state) => (event) => {
    switch (state) {
      case 'checked':
        updateSelected('remove', dataPaginated.content)
        break
      case 'unchecked':
      case 'indeterminate':
        updateSelected('add', dataPaginated.content)
        break
    }
  }

  const checkAllState = dataPaginated.content.every((url) => url.selected)
    ? 'checked'
    : dataPaginated.content.some((url) => url.selected)
    ? 'indeterminate'
    : 'unchecked'

  return (
    <div className="omm-table">
      <div className="omm-table__nav">
        <Input
          onChange={(value) => setQuery(value)}
          placeholder="Filter by keywords"
          theme={INPUT_THEME.SEARCH}
          value={query}
          iconStyle={{ left: 12, width: 16 }}
        />
        <Select
          placeholder="Filter by Score"
          options={SCORE_OPTIONS}
          onChange={(score) => setScoreFilter(score?.key ?? null)}
          isClearable={true}
        />
        <CheckBox
          label="Show extra details"
          checked={showExtraColumns}
          onChange={() => setShowExtraColumns((prev) => !prev)}
        />
        <Pagination
          options={{ length: dataPaginated.quantity }}
          page={dataPaginated.number - 1}
          onPageChange={(page) => toPage(page.label)}
        />
      </div>
      <table style={{ '--columns-size': columnsSize }}>
        <Header
          columns={columns}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          checkAllState={checkAllState}
          handleCheckAll={handleCheckAll}
          isCheckboxDisabled={isCheckboxDisabled}
        />
        <tbody className="mac-scrollbar">
          <For each="item" of={dataPaginated.content}>
            <Row
              key={`${item.canonicalUrl}${item.eventsPct}${item.usersPct}`}
              item={item}
              columns={columns}
              handleCheckbox={handleCheckbox}
              isCheckboxDisabled={isCheckboxDisabled}
            />
          </For>
        </tbody>
      </table>
    </div>
  )
}

export default Table
